<template>
    <!-- Bulk Upload Commentary Preview Dialog -->
    <lb-dialog 
      v-model="internalValue" 
      heading="Bulk Upload Commentary Preview"
      :width="dialogWidth"
      :loading="loading"
    >
      <template v-slot:body>
        <div v-if="hasData" class="mt-2">
          <lb-datatablenew 
            :headers="headers" 
            :items="fileData"
            :enableslot="['message', 'ignore']" 
            :loading="loading"
          >
            <!-- Ignore Column Slot -->
            <template v-slot:ignore="{ item }">
              <td style="width:30px;">
                <v-icon v-if="item.ignore" color="error">
                  mdi-close-circle
                </v-icon>
                <v-icon v-else-if="!item.ignore" color="success">
                  mdi-check-circle
                </v-icon>
              </td>
            </template>
  
            <!-- Message Column Slot -->
            <template v-slot:message="{ item }">
              <td :class="{ 'error--text': item.ignore }">
                <span>{{ item.message || '' }}</span>
              </td>
            </template>
          </lb-datatablenew>
        </div>
  
        <!-- Empty State -->
        <div v-else class="text-center pa-4 grey--text">
          No data to preview
        </div>
      </template>
  
      <!-- Dialog Actions -->
      <template v-slot:actions v-if="showActions && hasData">
        <v-spacer></v-spacer>
        <v-btn 
          id="previewBulkAddCommentarySubmitButton"
          color="primary"
          class="ml-2"
          :disabled="isSubmitDisabled"
          @click="handleSubmit"
        >
          <span>Submit</span>
        </v-btn>
      </template>
    </lb-dialog>
  </template>
  
  <script>
  /**
   * BulkUploadPreviewDialog Component
   * 
   * Displays a preview of bulk commentary upload data before submission.
   * Shows validation status for each row and allows users to proceed with upload.
   * 
   * @component
   * @example
   * <BulkUploadPreviewDialog
   *   v-model="previewDialog"
   *   :file-data="bulksubmitfiledata"
   *   :headers="bulkUploadCommentaryHeaders"
   *   :loading="bulksubmitloading"
   *   :is-preparer="ispreparer"
   *   :workflows="workflows"
   *   @submit="bulkUploadCommentaryByGL(true)"
   * />
   */
  export default {
    name: 'BulkUploadPreviewDialog',
  
    props: {
      /**
       * Controls dialog visibility (v-model)
       * @type {Boolean}
       */
      value: {
        type: Boolean,
        default: false
      },
  
      /**
       * Bulk upload file data to preview
       * Object where keys are row identifiers and values contain row data
       * @type {Object}
       */
      fileData: {
        type: Object,
        default: () => ({})
      },
  
      /**
       * Table headers configuration
       * @type {Array}
       */
      headers: {
        type: Array,
        default: () => []
      },
  
      /**
       * Loading state for the dialog and table
       * @type {Boolean}
       */
      loading: {
        type: Boolean,
        default: false
      },
  
      /**
       * Whether current user is a preparer
       * Used to determine if submit button should be shown
       * @type {Boolean}
       */
      isPreparer: {
        type: Boolean,
        default: false
      },
  
      /**
       * Workflow configuration object
       * Contains flags for different workflow permissions
       * @type {Object}
       * @property {Boolean} lcp - Local control proforma enabled
       * @property {Boolean} lmp - Local management proforma enabled
       * @property {Boolean} lcf - Local control forecast enabled
       * @property {Boolean} lmf - Local management forecast enabled
       */
      workflows: {
        type: Object,
        default: () => ({
          lcp: true,
          lmp: true,
          lcf: true,
          lmf: true
        })
      }
    },
  
    data() {
      return {
        /**
         * Internal value for v-model support
         */
        internalValue: this.value
      };
    },
  
    computed: {
      /**
       * Check if there is data to display
       * @returns {Boolean}
       */
      hasData() {
        return Object.keys(this.fileData || {}).length > 0;
      },
  
      /**
       * Calculate dialog width based on data availability
       * @returns {String} Width in pixels
       */
      dialogWidth() {
        return this.hasData ? '800' : '400';
      },
  
      /**
       * Determine if actions section should be shown
       * Only show for preparers
       * @returns {Boolean}
       */
      showActions() {
        return this.isPreparer;
      },
  
      /**
       * Determine if submit button should be disabled
       * Based on preparer status and workflow permissions
       * @returns {Boolean}
       */
      isSubmitDisabled() {
        if (!this.isPreparer) {
          return true;
        }
  
        // Check if at least one workflow is enabled
        const hasAnyWorkflowEnabled = 
          this.workflows.lcp || 
          this.workflows.lmp || 
          this.workflows.lcf || 
          this.workflows.lmf;
  
        return !hasAnyWorkflowEnabled;
      }
    },
  
    watch: {
      /**
       * Watch for external value changes
       * Updates internal state when parent changes value
       */
      value(newVal) {
        this.internalValue = newVal;
      },
  
      /**
       * Watch for internal value changes
       * Emits input event to update parent v-model
       */
      internalValue(newVal) {
        this.$emit('input', newVal);
      }
    },
  
    methods: {
      /**
       * Handle submit button click
       * Emits submit event to parent component
       * @emits submit
       */
      handleSubmit() {
        this.$emit('submit');
      }
    }
  };
  </script>
  
  <style scoped>
  /**
   * Styles for bulk upload preview dialog
   */
  
  /* Ensure proper spacing for error messages */
  .error--text {
    color: #ff5252 !important;
  }
  
  /* Success icon color */
  .v-icon.success--text {
    color: #4caf50 !important;
  }
  
  /* Error icon color */
  .v-icon.error--text {
    color: #ff5252 !important;
  }
  
  /* Table cell width constraint for icons */
  td[style*="width:30px"] {
    text-align: center;
    vertical-align: middle;
  }
  </style>